import { ReactNode, useState } from 'react';

import {
  buildSeededMeshConfig,
  FeaturedIcon,
  MeshGradient,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';

import { getPlaylistImageUrl, usesBundledArtwork } from '../utils/playlist.utils';

const PRIORITIES_STRINGS = appStrings.modules.priorities;

type PlaybookCardImageProps = Readonly<{
  playlist: PriorityPlaylist;
  badgeIcon?: ReactNode;
}>;

export const PlaybookCardImage = ({ playlist, badgeIcon }: PlaybookCardImageProps) => {
  const [imageError, setImageError] = useState(false);

  const imageUrl = getPlaylistImageUrl(playlist);
  const isBundledArtwork = usesBundledArtwork(playlist);
  const showImage = !!imageUrl && !imageError;

  return (
    <Box sx={containerSx} data-bundled-artwork={isBundledArtwork || undefined}>
      {showImage ? (
        <Box
          component="img"
          src={imageUrl}
          alt=""
          draggable={false}
          sx={imageSx}
          onError={() => setImageError(true)}
        />
      ) : (
        <MeshGradient config={buildSeededMeshConfig(playlist.id)} />
      )}

      <Box sx={featuredIconPositionSx}>
        <FeaturedIcon
          iconName="BookOpen01"
          size="md"
          backgroundColor="Brand/600"
          borderColor="Colors/Foreground/fg-primary"
          color="Colors/Base/Black"
        />
      </Box>

      <Chip
        variant="pill"
        size="sm"
        label={PRIORITIES_STRINGS.playlistCard.tasksCount(playlist.total_tasks)}
        sx={taskCountChipSx}
      />

      <Box sx={badgeSx}>
        {badgeIcon}
        <Typography variant="text-sm-medium" sx={badgeLabelSx}>
          {PRIORITIES_STRINGS.playbookPage.playbookBadge}
        </Typography>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  backgroundColor: backrs.colors['Beige/50'],
  '&[data-bundled-artwork]': { backgroundColor: backrs.colors['Blue/50'] },
  '&[data-bundled-artwork] img': { objectFit: 'contain' },
});

const imageSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

const featuredIconPositionSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-xl'],
  left: backrs.spacing['spacing-xl'],
});

const taskCountChipSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-xl'],
  right: backrs.spacing['spacing-xl'],
  color: backrs.colors['Colors/Text/text-secondary'],
  backgroundColor: backrs.colors['Gray/50'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const badgeSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  bottom: backrs.spacing['spacing-lg'],
  left: backrs.spacing['spacing-lg'],
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary']}`,
  borderRadius: backrs.radius['radius-md'],
  paddingX: backrs.spacing['spacing-md'],
  paddingY: backrs.spacing['spacing-xs'],
});

const badgeLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  whiteSpace: 'nowrap',
});
